import { Component, input, output, inject, computed, AfterViewInit, OnDestroy, ElementRef, PLATFORM_ID, signal } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { 
  PricingDisplayFeatureGroup, 
  PricingDisplayFeature, 
  PricingDisplayPlan 
} from '../../models';
import { PricingStoreService } from '../../services/pricing-store.service';
import { TooltipDirective } from '../tooltip/tooltip.component';
import { TruncatePipe } from '../../pipes';
import { ResponsiveService } from '../../services/responsive.service';

@Component({
  selector: 'features-grid',
  standalone: true,
  imports: [CommonModule, TooltipDirective, TruncatePipe],
  templateUrl: './features-grid.component.html',
  styleUrls: ['./features-grid.component.scss']
})
export class FeaturesGridComponent implements AfterViewInit, OnDestroy {
  // Inject store to read product data and mode
  private readonly store = inject(PricingStoreService);
  private readonly el = inject(ElementRef);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly responsive = inject(ResponsiveService);
  
  /** Reactive mobile detection */
  readonly isMobile = this.responsive.isMobile;

  // Signal inputs
  readonly showPlanNames = input<boolean>(true);
  readonly compactMode = input<boolean>(false);
  readonly billingPeriod = input<'monthly' | 'yearly'>('monthly');
  readonly showBillingToggle = input<boolean>(false);
  
  /** Display mode - passed from parent to avoid store coupling */
  readonly mode = input<'highlighted' | 'full'>('highlighted');
  
  /** Sticky header offset in pixels - used for section tracking */
  readonly stickyOffset = input<number>(170);

  // Signal outputs
  readonly onFeatureHighlight = output<PricingDisplayFeature>();
  readonly onBillingToggle = output<void>();
  /** Emits the name of the current section hidden by sticky header */
  readonly onActiveSectionChange = output<string>();
  
  // Internal state for active section
  private sectionObserver: IntersectionObserver | null = null;
  private readonly activeSectionName = signal<string>('');

  // Read data from store - avoids input timing issues
  private readonly selectedProduct = computed(() => this.store.selectedProduct());
  
  readonly featureGroups = computed(() => this.selectedProduct()?.featureGroups ?? []);
  readonly ungroupedFeatures = computed(() => this.selectedProduct()?.ungroupedFeatures ?? []);
  readonly plans = computed(() => this.selectedProduct()?.plans ?? []);

  // Sorted features - computed from store data (immutable)
  readonly sortedFeatureGroups = computed(() => {
    return this.featureGroups()
      .map(group => ({
        ...group,
        features: [...group.features]
        /*.sort((a, b) => {
          // Highlighted features first
          if (a.highlight && !b.highlight) return -1;
          if (!a.highlight && b.highlight) return 1;
          return a.name.localeCompare(b.name);
        })*/
      }))
      .sort((a, b) => a.order - b.order);
  });

  readonly sortedUngroupedFeatures = computed(() => {
    const ungrouped = [...this.ungroupedFeatures()].sort((a, b) => {
      // Highlighted features first
      if (a.highlight && !b.highlight) return -1;
      if (!a.highlight && b.highlight) return 1;
      // Then by name
      return a.name.localeCompare(b.name);
    });
    
    return ungrouped;
  });

  // Computed properties for template usage
  readonly hasFeatures = computed(() => 
    this.sortedFeatureGroups().length > 0 || this.sortedUngroupedFeatures().length > 0
  );
  readonly hasGroupedFeatures = computed(() => this.sortedFeatureGroups().length > 0);
  readonly hasUngroupedFeatures = computed(() => this.sortedUngroupedFeatures().length > 0);
  readonly planWidth = computed(() => {
    const planCount = this.plans().length;
    return planCount > 0 ? 100 / planCount : 0;
  });

  // Computed properties for mode-based filtering
  readonly filteredFeatureGroups = computed(() => {
    if (this.mode() === 'highlighted') {
      return this.sortedFeatureGroups()
        .map(group => ({
          ...group,
          features: group.features.filter(feature => feature.highlight)
        }))
        .filter(group => group.features.length > 0);
    }
    return this.sortedFeatureGroups();
  });

  readonly filteredUngroupedFeatures = computed(() => {
    if (this.mode() === 'highlighted') {
      return this.sortedUngroupedFeatures().filter(feature => feature.highlight);
    }
    return this.sortedUngroupedFeatures();
  });

  readonly showGroups = computed(() => this.mode() === 'full' && this.filteredFeatureGroups().length > 0);
  readonly showUngrouped = computed(() => this.filteredUngroupedFeatures().length > 0);

  // Helper methods for template
  getFeatureValue(feature: PricingDisplayFeature, planCode: string): {
    enabled: boolean;
    value?: number;
    label?: string;
    hint?: string;
  } {
    return feature.planValues?.[planCode] || { enabled: false, label: '', hint: '' };
  }

  isFeatureEnabledForAllPlans(feature: PricingDisplayFeature): boolean {
    return this.plans().every(plan => this.getFeatureValue(feature, plan.code).enabled);
  }

  isFeatureEnabledForAnyPlan(feature: PricingDisplayFeature): boolean {
    return this.plans().some(plan => this.getFeatureValue(feature, plan.code).enabled);
  }

  getFeatureAvailabilityClass(feature: PricingDisplayFeature): string {
    if (this.isFeatureEnabledForAllPlans(feature)) {
      return 'available-all';
    } else if (this.isFeatureEnabledForAnyPlan(feature)) {
      return 'available-some';
    }
    return 'available-none';
  }

  emitFeatureHighlight(feature: PricingDisplayFeature) {
    this.onFeatureHighlight.emit(feature);
  }

  // TrackBy functions for performance
  trackByFeatureCode(index: number, feature: PricingDisplayFeature): string {
    return feature.code;
  }

  trackByGroupCode(index: number, group: PricingDisplayFeatureGroup): string {
    return group.id;
  }

  trackByPlanCode(index: number, plan: PricingDisplayPlan): string {
    return plan.code;
  }

  shouldShowFeatureLabel(feature: PricingDisplayFeature, planCode: string): boolean {
    const value = this.getFeatureValue(feature, planCode);
    return value.enabled && value.label && value.label !== '' ? true : false;
  }

  getFeatureIcon(feature: PricingDisplayFeature, planCode: string): string {
    const value = this.getFeatureValue(feature, planCode);
    if (!value.enabled) return '✗';
    if (value.label && value.label !== '') return '✓';
    return '✓';
  }

  getFeatureIconClass(feature: PricingDisplayFeature, planCode: string): string {
    const value = this.getFeatureValue(feature, planCode);
    if (!value.enabled) return 'icon-cross';
    if (value.label && value.label !== '') return 'icon-check-with-label';
    return 'icon-check';
  }

  // Lifecycle hooks for section tracking
  ngAfterViewInit() {
    if (!isPlatformBrowser(this.platformId)) return;
    
    // Small delay to ensure DOM is ready
    setTimeout(() => this.setupSectionObserver(), 100);
  }

  ngOnDestroy() {
    if (this.sectionObserver) {
      this.sectionObserver.disconnect();
      this.sectionObserver = null;
    }
  }

  private setupSectionObserver() {
    const offset = this.stickyOffset();
    
    // Create observer that triggers when element crosses the sticky header threshold
    this.sectionObserver = new IntersectionObserver(
      (entries) => {
        // Find sections that are intersecting (partially visible above threshold)
        const visibleSections: { name: string; top: number }[] = [];
        
        entries.forEach(entry => {
          const sectionName = (entry.target as HTMLElement).dataset['sectionName'];
          if (sectionName) {
            const rect = entry.boundingClientRect;
            // Section is "active" if its top is above the sticky header
            if (rect.top < offset) {
              visibleSections.push({ name: sectionName, top: rect.top });
            }
          }
        });
        
        // Sort by top position (descending) to get the most recently scrolled past
        visibleSections.sort((a, b) => b.top - a.top);
        
        if (visibleSections.length > 0) {
          const newSection = visibleSections[0].name;
          if (this.activeSectionName() !== newSection) {
            this.activeSectionName.set(newSection);
            this.onActiveSectionChange.emit(newSection);
          }
        }
      },
      {
        // Root margin: negative top margin equals sticky header height
        rootMargin: `-${offset}px 0px 0px 0px`,
        threshold: [0, 0.1, 0.5, 1]
      }
    );

    // Observe all section headers
    const sectionHeaders = this.el.nativeElement.querySelectorAll('[data-section-name]');
    sectionHeaders.forEach((header: Element) => {
      this.sectionObserver?.observe(header);
    });
  }

  /** Re-initialize observer (call when groups change) */
  refreshSectionObserver() {
    if (this.sectionObserver) {
      this.sectionObserver.disconnect();
    }
    this.setupSectionObserver();
  }
}
